// Dispatch Board write-back helpers.
// Every action updates the deals row (source of truth) AND inserts an audit
// row in dispatch_schedule so we have a chronological record of what happened.
// The reschedule webhook is fire-and-forget so a Zapier outage never blocks a
// portal action — Robert's UI stays responsive and the cascade retries async.

import { supabase, SUPABASE_URL } from "./supabase";
import type { Deal } from "./types";
import { notifyShannonOnSupplementRelease } from "./notifyShannonOnSupplementRelease";

const WEBHOOK_URL =
  (import.meta.env.VITE_DISPATCH_WEBHOOK_URL as string | undefined) ||
  `${SUPABASE_URL}/functions/v1/dispatch-webhook`;

type EventType = "pre_inspection" | "adjuster_meeting" | "reinspection_adjuster" | "build";

async function fireWebhook(payload: Record<string, unknown>) {
  if (!WEBHOOK_URL) return; // no-op until Zapier hook is wired
  try {
    await fetch(WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (err) {
    // Never block the UI on webhook failure. Surface in console for debugging.
    // eslint-disable-next-line no-console
    console.warn("Dispatch webhook failed (non-blocking):", err);
  }
}

function todayISO() {
  // Local-day yyyy-mm-dd (NOT UTC). Robert is in Georgia; using UTC would
  // roll over at 8 PM EDT and put "today" stops on the wrong day for ~4 hrs.
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Pad helper. */
function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Convert a local-time picker pair (yyyy-mm-dd + HH:mm) to a UTC ISO string
 *  suitable for insertion into a timestamptz column. Without the explicit Z,
 *  Postgres would interpret the bare timestamp as UTC and store the user's
 *  local time as if it were UTC — off by the timezone offset. */
function localToUtcIso(date: string, time: string): string {
  // `new Date("2026-04-17T14:00:00")` is parsed as LOCAL time per ES spec,
  // so .toISOString() then converts to the correct UTC instant.
  return new Date(`${date}T${time}:00`).toISOString();
}

/** Parse an ISO datetime (UTC from timestamptz) into { date, time } where
 *  both are LOCAL — for dispatch_schedule audit rows. The audit columns are
 *  `time without time zone`, conventionally local clock time.  */
function splitDateTime(iso: string | null): {
  date: string;
  time: string | null;
} {
  if (!iso) return { date: todayISO(), time: null };
  // A bare date (build_scheduled_date is a DATE column) is already the answer.
  // `new Date("2026-10-20")` parses as UTC midnight, which is the previous
  // evening in Georgia: the old code turned a Tue Oct 20 build into
  // "2026-10-19" + "20:00:00" on the audit row and in the homeowner text.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return { date: iso, time: null };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: todayISO(), time: null };
  // Local components, not UTC. Date is parsed in local zone via Date().
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`,
  };
}

// ---------------------------------------------------------------------------
// COMPLETE
// ---------------------------------------------------------------------------

export async function completePreInspection(
  deal: Deal,
  params: { findings: string },
  actorId: string | null,
) {
  const { date, time } = splitDateTime(deal.pre_inspection_date);
  const { error: dealErr } = await supabase
    .from("deals")
    .update({
      pre_inspection_completed: true,
      pre_inspection_completed_date: todayISO(),
      pre_inspection_findings: params.findings || null,
      stage: "pre_inspection_complete",
    })
    .eq("id", deal.id);
  if (dealErr) throw dealErr;

  const { error: schedErr } = await supabase.from("dispatch_schedule").insert({
    deal_id: deal.id,
    event_type: "pre_inspection",
    assigned_to: deal.pre_inspection_assigned_to,
    scheduled_date: date,
    scheduled_time: time,
    status: "completed",
    completion_notes: params.findings || null,
    created_by: actorId,
  });
  if (schedErr) throw schedErr;
}

export async function completeAdjusterMeeting(
  deal: Deal,
  params: { outcome: string; notes: string },
  actorId: string | null,
) {
  const { date, time } = splitDateTime(deal.adjuster_meeting_date);
  const { error: dealErr } = await supabase
    .from("deals")
    .update({
      adjuster_meeting_outcome: params.outcome,
    })
    .eq("id", deal.id);
  if (dealErr) throw dealErr;

  const { error: schedErr } = await supabase.from("dispatch_schedule").insert({
    deal_id: deal.id,
    event_type: "adjuster_meeting",
    assigned_to: deal.owner_id,
    scheduled_date: date,
    scheduled_time: time,
    status: "completed",
    completion_notes: [params.outcome, params.notes].filter(Boolean).join(" — "),
    created_by: actorId,
  });
  if (schedErr) throw schedErr;
}

export async function completeBuild(
  deal: Deal,
  params: { notes: string },
  actorId: string | null,
) {
  // May 7, 2026 — roof_complete stage retired; build-complete sends straight
  // to ready_for_supps (commission_ready_date is stamped by track_stage_change
  // trigger on entry, putting the deal in Chad's commission queue + Shannon's
  // supplements queue simultaneously).
  // Jun 22, 2026 — retail jobs skip supplements entirely and go straight to
  // collecting. The track_stage_change trigger enforces this on every write
  // path; mirror it here so the local write + Shannon notification agree.
  const targetStage = deal.is_retail === true ? "collecting" : "ready_for_supps";
  const { error: dealErr } = await supabase
    .from("deals")
    .update({
      build_completed_date: todayISO(),
      stage: targetStage,
      build_notes: params.notes || deal.build_notes,
    })
    .eq("id", deal.id);
  if (dealErr) throw dealErr;

  // Mirror TransitionButton: notify Shannon when a deal actually enters supps.
  // Retail deals route to collecting, so this supps-specific alert is skipped.
  if (targetStage === "ready_for_supps" && deal.stage !== "ready_for_supps") {
    void notifyShannonOnSupplementRelease(deal);
  }

  const { error: schedErr } = await supabase.from("dispatch_schedule").insert({
    deal_id: deal.id,
    event_type: "build",
    assigned_to: deal.owner_id,
    scheduled_date: deal.build_scheduled_date ?? todayISO(),
    scheduled_time: null,
    status: "completed",
    completion_notes: params.notes || null,
    created_by: actorId,
  });
  if (schedErr) throw schedErr;
}

// ---------------------------------------------------------------------------
// RESCHEDULE
// ---------------------------------------------------------------------------

export async function rescheduleEvent(
  deal: Deal,
  type: EventType,
  params: { newDate: string; newTime: string | null; reason: string },
  actorId: string | null,
) {
  // Mark the old scheduled row as rescheduled (audit breadcrumb) before we
  // shift the canonical date on deals. dispatch_schedule might be empty for
  // back-filled deals — that's fine, the insert below is what matters.
  const oldDateTime =
    type === "pre_inspection"
      ? deal.pre_inspection_date
      : type === "adjuster_meeting"
        ? deal.adjuster_meeting_date
        : type === "reinspection_adjuster"
          ? deal.reinspection_adjuster_meeting_date
          : deal.build_scheduled_date;
  const oldSplit = splitDateTime(oldDateTime);

  await supabase.from("dispatch_schedule").insert({
    deal_id: deal.id,
    event_type: type,
    assigned_to:
      type === "pre_inspection"
        ? deal.pre_inspection_assigned_to
        : deal.owner_id,
    scheduled_date: oldSplit.date,
    scheduled_time: oldSplit.time,
    status: "rescheduled",
    reschedule_reason: params.reason,
    created_by: actorId,
  });

  const newISO =
    params.newTime != null
      ? localToUtcIso(params.newDate, params.newTime)
      : params.newDate;

  const updates: Record<string, unknown> = {};
  if (type === "pre_inspection") {
    updates.pre_inspection_date = newISO;
  } else if (type === "adjuster_meeting") {
    updates.adjuster_meeting_date = newISO;
  } else if (type === "reinspection_adjuster") {
    updates.reinspection_adjuster_meeting_date = newISO;
  } else {
    // build is a date column (no time)
    updates.build_scheduled_date = params.newDate;
    updates.build_reschedule_count = (deal.build_reschedule_count ?? 0) + 1;
    updates.build_reschedule_reason = params.reason;
    updates.build_reschedule_last_at = new Date().toISOString();
  }

  const { error: dealErr } = await supabase
    .from("deals")
    .update(updates)
    .eq("id", deal.id);
  if (dealErr) throw dealErr;

  const newSplit = splitDateTime(newISO);
  const { error: insErr } = await supabase.from("dispatch_schedule").insert({
    deal_id: deal.id,
    event_type: type,
    assigned_to:
      type === "pre_inspection"
        ? deal.pre_inspection_assigned_to
        : deal.owner_id,
    scheduled_date: newSplit.date,
    scheduled_time: newSplit.time,
    status: "scheduled",
    reschedule_reason: params.reason,
    created_by: actorId,
  });
  if (insErr) throw insErr;

  // Fire cascade (homeowner text + calendar update). NOTE: no RM alert fires
  // here — that was a Slack DM, dead since Apr 2026. See dispatch-webhook v2.
  await fireWebhook({
    action: "reschedule",
    event_type: type,
    deal_id: deal.id,
    old_date: oldSplit.date,
    old_time: oldSplit.time,
    new_date: newSplit.date,
    new_time: newSplit.time,
    reason: params.reason,
    homeowner_name: deal.homeowner_name,
    homeowner_phone: deal.homeowner_phone,
    owner_id: deal.owner_id,
    actor_id: actorId,
  });
}

// ---------------------------------------------------------------------------
// SCHEDULE (from Unscheduled tab)
// ---------------------------------------------------------------------------

export async function schedulePreInspection(
  deal: Deal,
  params: { rsId: string; date: string; time: string | null },
  actorId: string | null,
) {
  const iso =
    params.time != null
      ? localToUtcIso(params.date, params.time)
      : params.date;

  const { error: dealErr } = await supabase
    .from("deals")
    .update({
      pre_inspection_date: iso,
      pre_inspection_assigned_to: params.rsId,
      pre_inspection_assigned_date: new Date().toISOString(),
    })
    .eq("id", deal.id);
  if (dealErr) throw dealErr;

  const { error: insErr } = await supabase.from("dispatch_schedule").insert({
    deal_id: deal.id,
    event_type: "pre_inspection",
    assigned_to: params.rsId,
    scheduled_date: params.date,
    scheduled_time: params.time,
    status: "scheduled",
    created_by: actorId,
  });
  if (insErr) throw insErr;

  await fireWebhook({
    action: "schedule_pre_inspection",
    deal_id: deal.id,
    rs_id: params.rsId,
    date: params.date,
    time: params.time,
    homeowner_name: deal.homeowner_name,
    homeowner_phone: deal.homeowner_phone,
    owner_id: deal.owner_id,
    actor_id: actorId,
  });
}

export async function scheduleAdjusterMeeting(
  deal: Deal,
  params: { date: string; time: string; adjusterName?: string; adjusterPhone?: string },
  actorId: string | null,
) {
  // `time` is the START of the insurance window (e.g., 10am for a 10am-1pm
  // slot). Adjusters usually arrive at that time; it can be updated if the
  // adjuster confirms a tighter time the night before.
  const iso = localToUtcIso(params.date, params.time);

  // Stage stays at adjuster_meeting_ready — the flag for "scheduled" is
  // adjuster_meeting_date being non-null, not a separate stage value.
  const updates: Record<string, unknown> = {
    adjuster_meeting_date: iso,
  };
  if (params.adjusterName) updates.adjuster_name = params.adjusterName;
  if (params.adjusterPhone) updates.adjuster_phone = params.adjusterPhone;

  const { error: dealErr } = await supabase
    .from("deals")
    .update(updates)
    .eq("id", deal.id);
  if (dealErr) throw dealErr;

  const { error: insErr } = await supabase.from("dispatch_schedule").insert({
    deal_id: deal.id,
    event_type: "adjuster_meeting",
    assigned_to: deal.pre_inspection_assigned_to ?? deal.owner_id,
    scheduled_date: params.date,
    scheduled_time: params.time,
    status: "scheduled",
    created_by: actorId,
  });
  if (insErr) throw insErr;

  await fireWebhook({
    action: "schedule_adjuster_meeting",
    deal_id: deal.id,
    date: params.date,
    time: params.time,
    homeowner_name: deal.homeowner_name,
    homeowner_phone: deal.homeowner_phone,
    owner_id: deal.owner_id,
    actor_id: actorId,
  });
}

export async function scheduleBuild(
  deal: Deal,
  params: { date: string; notes: string; endDate?: string | null },
  actorId: string | null,
) {
  // Hard gate: builds can only land on the calendar AFTER Chad approves and
  // the deal advances to `schedule_build`. Without this, any caller could
  // scoop an `install_meeting` (pre-submission) or `build_submitted` (sitting
  // in Chad's approval queue) deal onto the calendar without anyone reviewing
  // scope / install-meeting findings / PRE notes / pricing. The conditional
  // .eq("stage", "schedule_build") makes this atomic — a stage change racing
  // the modal also rejects, no client-side stale-state window.
  //
  // Admin override path: AdminControlsPanel writes `deal.stage` directly via
  // a stage-override action that logs to activity_log with a reason — this
  // function is intentionally NOT the path for emergency reroutes.
  const { data: updated, error: dealErr } = await supabase
    .from("deals")
    .update({
      build_scheduled_date: params.date,
      // Oct 26, 2026: the last day of a multi-day build (null = one day).
      build_end_date: params.endDate ?? null,
      stage: "build_scheduled",
      phase: "builds",
      build_notes: params.notes || deal.build_notes,
    })
    .eq("id", deal.id)
    .eq("stage", "schedule_build")
    .select("id");
  if (dealErr) throw dealErr;
  if (!updated || updated.length === 0) {
    // No row matched the (id, stage) pair. Either Chad hasn't approved yet,
    // the deal already advanced past schedule_build, or another dispatcher
    // beat us to it. Re-read so the error message reflects the real state.
    const { data: fresh } = await supabase
      .from("deals")
      .select("stage")
      .eq("id", deal.id)
      .maybeSingle();
    const stageLabel = fresh?.stage ?? deal.stage ?? "unknown";
    if (stageLabel === "build_submitted") {
      throw new Error(
        "Waiting on Chad's approval — this build is in his queue. Once he approves, you'll be able to schedule it.",
      );
    }
    if (stageLabel === "install_meeting") {
      throw new Error(
        "The RM hasn't submitted this build to Chad yet. The RM needs to submit before it can be scheduled.",
      );
    }
    if (stageLabel === "build_scheduled") {
      throw new Error(
        "This build is already on the calendar. Open the deal directly to reschedule.",
      );
    }
    throw new Error(
      `Build can't be scheduled — deal is in "${stageLabel}". Chad has to approve before Robert can put it on the calendar.`,
    );
  }

  const { error: insErr } = await supabase.from("dispatch_schedule").insert({
    deal_id: deal.id,
    event_type: "build",
    assigned_to: deal.owner_id,
    scheduled_date: params.date,
    scheduled_time: null,
    status: "scheduled",
    completion_notes: params.notes || null,
    created_by: actorId,
  });
  if (insErr) throw insErr;

  await fireWebhook({
    action: "schedule_build",
    deal_id: deal.id,
    date: params.date,
    end_date: params.endDate ?? null,
    notes: params.notes,
    homeowner_name: deal.homeowner_name,
    homeowner_phone: deal.homeowner_phone,
    owner_id: deal.owner_id,
    actor_id: actorId,
  });
}
